import React from 'react';
import { Tooltip } from './Tooltip';

/**
 * A CONTROL THAT CAN'T DO ITS THING SAYS WHY — IT NEVER PRETENDS.
 *
 * The class this ends: buttons that only fired a toast ("Refund issued", "Customer profile
 * updated", "Tickets resent") while nothing was written — 50 of them across the console, plus 40
 * "… opened" toasts that opened nothing. An operator believed them.
 *
 * `reason` null → the control is passed through untouched. `reason` a sentence → the control is
 * dimmed and inert, the click is swallowed, and the sentence shows — at once — on hover, keyboard
 * focus AND the click itself, in the console's own Tooltip, never the browser's
 * `title`, which waited a second, looked like another app, and did nothing on touch. A native
 * `disabled` fires no events, so it could never explain itself — hence aria-disabled on a focusable
 * wrapper. Inertness derives from the reason, so nothing can be gated silently.
 *
 *   <Gate reason={canRefund ? null : 'Refunds are issued from the order — open it to refund.'}>
 *     <Button …>Refund</Button>
 *   </Gate>
 */
export const Gate = ({ reason, children, className = '' }) => {
  if (!reason) return children;
  const swallow = (e) => { e.preventDefault(); e.stopPropagation(); };
  return (
    <Tooltip text={reason} delay={0} openOnClick>
    <span
      role="note"
      aria-disabled="true"
      tabIndex={0}
      aria-label={reason}
      onClickCapture={swallow}
      onKeyDownCapture={(e) => { if (e.key === 'Enter' || e.key === ' ') swallow(e); }}
      className={`relative inline-flex cursor-not-allowed opacity-45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F2937]/20 rounded ${className}`}
    >
      <span className="pointer-events-none inline-flex">{children}</span>
    </span>
    </Tooltip>
  );
};
